import { SessionStatusEnum, useSession, useSlots } from '@/hooks';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { getDatePickerProps } from '@/utils/getDatePickerProps';
import { format, startOfMonth, parseISO } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

const DEFAULT_DATE = new Date();
const DEFAULT_TIMEZONE = 'America/Los_Angeles';

export const useScanDates = () => {
  const {
    location,
    slots,
    closestAvailableDate,
    errorsScanDetails,
    selectedDate,
    sku,
    setSelectedDate,
    setErrorModal,
  } = usePrenuvoStore(
    useShallow((state) => ({
      location: state.scan.location,
      slots: state.scan.slots,
      sku: state.scan.sku,
      closestAvailableDate: state.scan.closestAvailableDate,
      errorsScanDetails: state.scan.errors,
      selectedDate: state.scan.date,
      setSelectedDate: state.scan.methods.setDate,
      setErrorModal: state.ui.methods.setErrorModal,
    })),
  );

  const { getSlots, isSlotsLoading, slotsError } = useSlots();
  const isSlotsError = !!slotsError;
  const { sessionStatus } = useSession();

  useEffect(() => {
    // This is a workaround for the date picker component, since it doesn’t provide a way to disable the button directly.
    const arrowBackElement: HTMLElement | null = document.querySelector(
      '[aria-label="Previous Month"]',
    );
    if (!closestAvailableDate || !arrowBackElement || !selectedDate) return;

    // Parse closestAvailableDate using parseISO for consistent parsing
    const normalizedClosestDate = startOfMonth(parseISO(closestAvailableDate));
    const normalizedSelectedDate = startOfMonth(selectedDate);
    const normalizedCurrentDate = startOfMonth(new Date());

    // Check if both the current date and selected date are before or equal to the closest available date
    const isBeforeClosestDate =
      normalizedCurrentDate.getTime() <= normalizedClosestDate.getTime() &&
      normalizedSelectedDate.getTime() <= normalizedClosestDate.getTime();

    // Hide or show the "Previous Month" button based on the comparison
    arrowBackElement.style.display = isBeforeClosestDate ? 'none' : 'flex';
  }, [closestAvailableDate, selectedDate]);

  useEffect(() => {
    isSlotsError &&
      setErrorModal('Error', 'There was an error loading the available slots.', {
        action: () => getSlots(undefined, { callerContext: 'retry' }),
        text: 'Retry',
      });

    //TODO: move doNotFetchSlots to session slice
    const doNotFetchSlots =
      sessionStatus === SessionStatusEnum.LOADING || isSlotsLoading || isSlotsError || slots.length;

    if (doNotFetchSlots) return;

    getSlots(selectedDate, { callerContext: 'scanDate' });
  }, [
    getSlots,
    isSlotsError,
    isSlotsLoading,
    selectedDate,
    sessionStatus,
    setErrorModal,
    slots.length,
  ]);

  const handleDateChange = (
    date: Date | undefined,
    context: string,
    disableCache: boolean = false,
  ) => {
    if (context === 'monthChange' && date) date.setDate(1);
    setSelectedDate(date);
    getSlots(date, { callerContext: context, disableCache });
  };

  const onDateChange = (date: Date | undefined) => {
    handleDateChange(date, 'dateChange');
  };

  const onMonthChange = (date: Date | undefined) => {
    handleDateChange(date, 'monthChange', true);
    const month = date?.toLocaleString('default', { month: 'long' });
    const year = date?.getFullYear();
    if (month && year) {
      document
        ?.getElementById('month-change-announcer')
        ?.setAttribute('aria-label', `Navigating to ${month} ${year}`);
    }
  };


  const getBaseDates = () => {
    const tzClosestAvailableDate = fromZonedTime(
      Math.min(Number(closestAvailableDate), Number(slots[0]?.date)) || DEFAULT_DATE,
      location?.timezone || DEFAULT_TIMEZONE,
    );

    const datePickerFieldProps = getDatePickerProps(tzClosestAvailableDate);

    return {
      tzClosestAvailableDate,
      datePickerFieldProps,
    };
  };

  const { tzClosestAvailableDate, datePickerFieldProps } = getBaseDates();

  return {
    location,
    selectedDate,
    tzClosestAvailableDate,
    datePickerFieldProps,
    slots,
    isSlotsLoading,
    errorsScanDetails,
    sku,
    onDateChange,
    onMonthChange,
  };
};
